import { $isLinkNode } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { $isMarkNode } from '@lexical/mark';
import { $isHeadingNode, HeadingNode } from '@lexical/rich-text';
import {
  $createRangeSelection,
  $isTextNode,
  ElementNode,
  LexicalNode,
  RangeSelection,
  $isParagraphNode,
  ParagraphNode,
} from 'lexical';
import { partition } from 'lodash';
import { $getAncestor, $getDirectChild } from '../../lexicalUtils';
import { $isCustomListItemNode, CustomListItemNode } from '../CustomListItemNode';
import { $createNoWrapNode, $isNoWrapNode, NoWrapNode } from './NoWrapNode';

export * from './removeEmptyNoWrapNodesByKeys';

/**
 * Return the **outermost** LinkNode or MarkNode ancestor (furthest from the leaf).
 * If none found – returns null.
 */
function getOutermostWrapper(node: LexicalNode | null): LexicalNode | null {
  let outer: LexicalNode | null = null;
  let current = node;
  while (current) {
    if ($isLinkNode(current) || $isMarkNode(current)) {
      outer = current;
    }
    current = current.getParent();
  }

  return outer;
}

/** Word boundary helpers */
const WS = /\s/;

const wordStart = (text: string, offset: number): number => {
  while (offset > 0 && !WS.test(text[offset - 1])) {
    offset--;
  }

  return offset;
};

const wordEnd = (text: string, offset: number): number => {
  while (offset < text.length && !WS.test(text[offset])) {
    offset++;
  }

  return offset;
};

function $normalizeSelection(selection: RangeSelection): RangeSelection {
  // A new selection is used to avoid side-effects of flipping the given selection
  const forwardSelection = $createRangeSelection();
  const [startPoint, endPoint] = selection.isBackward()
    ? [selection.focus, selection.anchor]
    : [selection.anchor, selection.focus];

  forwardSelection.anchor.set(
    startPoint.key,
    startPoint.offset,
    startPoint.type,
  );

  forwardSelection.focus.set(
    endPoint.key,
    endPoint.offset,
    endPoint.type);

  const anchorNode = forwardSelection.anchor.getNode();
  const focusNode = forwardSelection.focus.getNode();

  // Detect outermost mark/link for both ends
  const anchorOut = getOutermostWrapper(anchorNode);
  const focusOut = getOutermostWrapper(focusNode);

  let anchorKey = forwardSelection.anchor.key;
  let anchorOffset = forwardSelection.anchor.offset;
  let anchorType: 'text' | 'element' = $isTextNode(anchorNode) ? 'text' : 'element';

  if (anchorOut) {
    anchorKey = anchorOut.getKey();
    anchorOffset = 0; // start of element
    anchorType = 'element';
  } else if ($isTextNode(anchorNode)) {
    anchorOffset = wordStart(anchorNode.getTextContent(), forwardSelection.anchor.offset);
  }

  let focusKey = forwardSelection.focus.key;
  let focusOffset = forwardSelection.focus.offset;
  let focusType: 'text' | 'element' = $isTextNode(focusNode) ? 'text' : 'element';

  if (focusOut) {
    focusKey = focusOut.getKey();
    focusOffset = (focusOut as ElementNode).getChildrenSize(); // after last child
    focusType = 'element';
  } else if ($isTextNode(focusNode)) {
    focusOffset = wordEnd(focusNode.getTextContent(), forwardSelection.focus.offset);
  }

  forwardSelection.anchor.set(anchorKey, anchorOffset, anchorType);
  forwardSelection.focus.set(focusKey, focusOffset, focusType);

  return forwardSelection;
}

function $removeNoWrapNode(node: NoWrapNode): void {
  const children = node.getChildren();
  let target: LexicalNode | null = null;

  for (const element of children) {
    const child = element;
    if (target === null) {
      node.insertBefore(child);
    } else {
      target.insertAfter(child);
    }
    target = child;
  }
  node.remove();
}

function $isNodeWithTextContent(node: LexicalNode): boolean {
  return node.getTextContent().trim().length !== 0;
}

/** Remove any `NoWrapNode` that currently contains the given nodes. */
export function $unwrapNodes(selection: RangeSelection): boolean {
  const nodes = selection.getNodes().filter($isNodeWithTextContent);
  const allWrapped = nodes.every(node => ($getAncestor(node, $isNoWrapNode) ?? $getDirectChild(node, $isNoWrapNode)));

  for (const node of nodes) {
    const parent = node.getParent();
    if ($isNoWrapNode(parent)) {
      $removeNoWrapNode(parent);
    }
  }

  return allWrapped;
}

const $isBlockNode = (node: LexicalNode): node is ParagraphNode | HeadingNode | ListNode => {
  return $isParagraphNode(node) || $isHeadingNode(node) || $isListNode(node);
};

/** Wrap the current selection in a new NoWrapNode */
export function $wrapNodes(selection: RangeSelection): void {
  const forwardSelection = $normalizeSelection(selection);
  const nodes = forwardSelection.extract().filter($isNodeWithTextContent);

  const topLevelNodes = nodes.filter((node) => {
    const parent = node.getParent();

    return !parent || !nodes.find(n => n.getKey() === parent.getKey());
  });

  const [customListItemNodes, otherTopLevelNodes] = partition(topLevelNodes, node => $isCustomListItemNode(node));

  if (customListItemNodes.length) {
    customListItemNodes.forEach((node: CustomListItemNode) => {
      const customListNodeWrapper = $createNoWrapNode();
      const children = node.getChildren();

      children.forEach((child) => {
        customListNodeWrapper.append(child);
      });

      node.append(customListNodeWrapper);
    });
  }

  if (!otherTopLevelNodes.length) {
    return;
  }

  const [blockNodes, inlineNodes] = partition(otherTopLevelNodes, $isBlockNode);

  if (inlineNodes.length) {
    const inlineWrapper = $createNoWrapNode();
    inlineNodes[0].insertBefore(inlineWrapper);
    inlineNodes.forEach(node => inlineWrapper.append(node));
  }

  blockNodes.forEach((blockNode) => {
    const children = blockNode.getChildren();
    if (!children.length) {
      return;
    }
    const innerWrapper = $createNoWrapNode();
    children[0].insertBefore(innerWrapper);
    children.forEach(child => innerWrapper.append(child));
  });

}

